import { Box3, OrthographicCamera, PerspectiveCamera, Vector3 } from "three";

export async function fitCameraToBoundingBox({
  camera,
  boundingBox,
  offset,
  width,
  height,
  initialY,
  direction,
  target,
  smooth,
  setCameraInPositionAndTarget,
}: {
  camera: OrthographicCamera | PerspectiveCamera;
  boundingBox: Box3;
  offset: number;
  width: number;
  height: number;
  initialY: number;
  direction?: Vector3;
  /** Point to look at. Defaults to the center of the box; the whole box is still fitted around it. */
  target?: Vector3;
  smooth: number;
  setCameraInPositionAndTarget: (
    cameraName: "perspective" | "orthographic",
    cameraPosition?: { x: number; y: number; z: number },
    controlsTarget?: { x: number; y: number; z: number },
    smooth?: number,
  ) => Promise<void>;
}) {
  offset = offset || 1.25;

  if (!width || !height) {
    console.warn("Viewport has no size, cannot fit the camera");
    return;
  }

  if (boundingBox.isEmpty()) {
    console.warn("Bounding box is empty, cannot fit the camera");
    return;
  }

  const center = target?.clone() ?? boundingBox.getCenter(new Vector3());

  if (camera instanceof PerspectiveCamera) {
    const forward = (direction?.clone() ?? new Vector3(0, (initialY ?? 0) / 10, 1)).normalize();
    const distance = computeFitDistance(
      boundingBox,
      center,
      forward,
      camera.fov,
      width / height,
      offset,
    );
    const position = center.clone().sub(forward.multiplyScalar(distance));

    await setCameraInPositionAndTarget("perspective", position, center, smooth);
  } else {
    const size = boundingBox.getSize(new Vector3());
    const ratio = height / width;
    const maxDim = Math.max(size.x, size.y, size.z) * ratio;

    const cam = camera as OrthographicCamera;
    cam.zoom = (width / maxDim) * 0.9;
    camera.position.x = center.x;
    camera.position.y = initialY * 0.1;
    camera.position.z = -center.z - maxDim;
    camera.updateProjectionMatrix();
  }
}

/**
 * Distance from `center` (along `-forward`) at which every corner of the box
 * fits inside the frustum, checking both the horizontal and the vertical FOV.
 * `center` doesn't have to be the center of the box.
 * `offset` is the frame margin: 1.25 makes the box take 1/1.25 of the limiting axis.
 */
function computeFitDistance(
  boundingBox: Box3,
  center: Vector3,
  forward: Vector3,
  fovInDegrees: number,
  aspect: number,
  offset: number,
): number {
  const worldUp = Math.abs(forward.y) > 0.999 ? new Vector3(0, 0, 1) : new Vector3(0, 1, 0);
  const right = new Vector3().crossVectors(forward, worldUp).normalize();
  const up = new Vector3().crossVectors(right, forward).normalize();

  const tanV = Math.tan((fovInDegrees * Math.PI) / 180 / 2);
  const tanH = tanV * aspect;

  const { min, max } = boundingBox;
  const corner = new Vector3();
  let distance = 0;

  for (let i = 0; i < 8; i++) {
    corner.set(i & 1 ? max.x : min.x, i & 2 ? max.y : min.y, i & 4 ? max.z : min.z).sub(center);

    const x = Math.abs(corner.dot(right)) * offset;
    const y = Math.abs(corner.dot(up)) * offset;
    const z = corner.dot(forward); // > 0 means farther than the center

    distance = Math.max(distance, x / tanH - z, y / tanV - z);
  }

  return distance;
}
